import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FlexBox } from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import { aggregateTaxAwarePrices } from '@vp/om-tax-display';
import { TaxAwarePriceRow } from '@vp/component-library';
import { pricingSelectors } from '../../state/pricing';
import { TaxDisplayContext } from '../shared/taxDisplay/TaxDisplayContext';
import IfDebug from '../debugTooling/IfDebug';
import useSiteData from '../../hooks/useSiteData';
import { cartItemSelectors } from '../../state/cartItems';
import { DebugTaxInfo } from '../debugTooling/DebugTaxInfo';

export const ItemGroupPricingSummary = ({ groupId }) => {
  const [cartMessages] = useTranslations(['cartMessages']);
  const { locale } = useSiteData();

  const currency = useSelector(state =>
    pricingSelectors.selectCurrency(state.pricing)
  );
  const fractionDigits = useSelector(state =>
    pricingSelectors.selectFractionDigits(state.pricing)
  );
  const itemGroupTotalLabel = useSelector(state =>
    cartItemSelectors.selectItemGroupTotalLabel(
      state.cartItems,
      groupId,
      locale
    )
  );

  const { clusivitySeparatedItemGroupPrices, taxDisplayContext } =
    useContext(TaxDisplayContext);
  const itemGroupPrices = clusivitySeparatedItemGroupPrices[groupId] || [];
  const aggregatedItemGroupPrice = aggregateTaxAwarePrices(
    itemGroupPrices,
    'itemGroup',
    'detailed',
    [],
    taxDisplayContext
  );

  return (
    <>
      <TaxAwarePriceRow
        rowLabel={itemGroupTotalLabel || cartMessages.groupTotal}
        labelFormat={{
          fontSize: 2,
          fontWeight: 'bold',
        }}
        priceFormat={{
          fontWeight: 'bold',
          fontSize: 0,
        }}
        fractionDigits={fractionDigits}
        listCents={aggregatedItemGroupPrice.taxAwareListCents}
        currencyCode={currency}
        locale={locale}
        discountedCents={aggregatedItemGroupPrice.taxAwareDiscountedCents}
        taxes={[
          ...aggregatedItemGroupPrice.includedTaxes.map(tax => ({
            ...tax,
            labelFormat: { textColor: 'black', fontSize: -1 },
          })),
          ...aggregatedItemGroupPrice.excludedTaxes.map(tax => ({
            ...tax,
            labelFormat: { textColor: 'black', fontSize: -1 },
          })),
        ]}
      />
      <IfDebug>
        <FlexBox justifyContent="right">
          <DebugTaxInfo
            aggregatedTaxAwarePrice={aggregatedItemGroupPrice}
            header="Group"
          />
        </FlexBox>
      </IfDebug>
    </>
  );
};

ItemGroupPricingSummary.propTypes = {
  groupId: PropTypes.string.isRequired,
};
